/**
 * The sessions' pure model (1.M-2 stage s3, deliverables 20 and 21): the options the "When" field
 * offers in the route's order, their labels, the "New session" request and the "Edit session"
 * requests. Nothing here reads or writes; the forms and hooks live in `sessions.ts`,
 * `new-session-form.tsx` and `edit-session-form.tsx`.
 */
import {
  ApiError,
  type SessionCreateV1,
  type SessionUpdateV1,
  type SessionV1,
} from '@codex/contracts';

export type SessionStatusChoice = 'planned' | 'prepped' | 'played';

export const STATUS_CHOICES: readonly {
  readonly value: SessionStatusChoice;
  readonly label: string;
}[] = [
  { value: 'planned', label: 'Planned' },
  { value: 'prepped', label: 'Prepped' },
  { value: 'played', label: 'Played' },
];

export interface SessionRow {
  readonly id: string;
  readonly title: string;
  readonly number: number | null;
  readonly realDate: string | null;
  readonly status: SessionV1['status'];
}

/** "Session <n> · <title>", or "<title> (no number yet)". */
export function sessionOptionLabel(session: Pick<SessionRow, 'number' | 'title'>): string {
  return session.number === null
    ? `${session.title} (no number yet)`
    : `Session ${session.number} · ${session.title}`;
}

/** Sessions from the loaded pages, in the route's order, each once. */
export function sessionRows(pages: readonly (readonly SessionV1[])[]): SessionRow[] {
  const seen = new Set<string>();
  const rows: SessionRow[] = [];
  for (const page of pages) {
    for (const s of page) {
      if (seen.has(s.document.id)) continue;
      seen.add(s.document.id);
      rows.push({
        id: s.document.id,
        title: s.document.title,
        number: s.number,
        realDate: s.realDate,
        status: s.status,
      });
    }
  }
  return rows;
}

/** Session number by document id, for the sessions that have one. */
export function sessionNumbers(rows: readonly SessionRow[]): ReadonlyMap<string, number> {
  const map = new Map<string, number>();
  for (const row of rows) if (row.number !== null) map.set(row.id, row.number);
  return map;
}

export interface NewSessionForm {
  readonly title: string;
  readonly number: string;
  readonly realDate: string;
  readonly status: SessionStatusChoice;
}

export function emptyNewSessionForm(): NewSessionForm {
  return { title: '', number: '', realDate: '', status: 'planned' };
}

export interface SessionFormProblem {
  readonly field: 'title' | 'number' | 'realDate';
  readonly message: string;
}

export const TITLE_REQUIRED = 'Enter a title.';
export const NUMBER_INVALID = 'The number is a whole number from 1.';
export const NUMBER_REQUIRED = 'A played session needs its number.';
export const DATE_INVALID = 'Played on is a date.';

const WHOLE = /^[0-9]+$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;

function parseNumber(text: string): number | null | 'bad' {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  if (!WHOLE.test(trimmed)) return 'bad';
  const n = Number(trimmed);
  return Number.isSafeInteger(n) && n >= 1 ? n : 'bad';
}

type Checked =
  | {
      readonly ok: true;
      readonly title: string;
      readonly number: number | null;
      readonly realDate: string;
    }
  | { readonly ok: false; readonly problem: SessionFormProblem };

/** The shared rules of both forms: a title, a whole number, a date, and Played needs a number. */
function checkSessionForm(form: NewSessionForm): Checked {
  const title = form.title.trim();
  if (title === '') return { ok: false, problem: { field: 'title', message: TITLE_REQUIRED } };
  const number = parseNumber(form.number);
  if (number === 'bad') return { ok: false, problem: { field: 'number', message: NUMBER_INVALID } };
  if (form.status === 'played' && number === null) {
    return { ok: false, problem: { field: 'number', message: NUMBER_REQUIRED } };
  }
  const realDate = form.realDate.trim();
  if (realDate !== '' && !DATE.test(realDate)) {
    return { ok: false, problem: { field: 'realDate', message: DATE_INVALID } };
  }
  return { ok: true, title, number, realDate };
}

export type NewSessionResult =
  | { readonly ok: true; readonly body: SessionCreateV1 }
  | { readonly ok: false; readonly problem: SessionFormProblem };

/** The `sessions.create` body of a form, or the first problem. Optional fields are omitted. */
export function newSessionRequest(form: NewSessionForm): NewSessionResult {
  const checked = checkSessionForm(form);
  if (!checked.ok) return checked;
  const { title, number, realDate } = checked;
  const body: { -readonly [K in keyof SessionCreateV1]: SessionCreateV1[K] } = { title };
  if (number !== null) body.number = number;
  if (realDate !== '') body.realDate = realDate;
  body.status = form.status;
  return { ok: true, body };
}

export const SESSION_REFUSED_TEXT = 'Check the title and number.';
export const SESSION_FAILED_TEXT = "The session couldn't be created. Try again.";

/** A refused or failed `sessions.create` as the form's alert. */
export function newSessionError(error: unknown): string {
  return error instanceof ApiError && error.status === 422
    ? SESSION_REFUSED_TEXT
    : SESSION_FAILED_TEXT;
}

export interface EditSessionForm {
  readonly title: string;
  readonly number: string;
  readonly realDate: string;
  readonly status: SessionStatusChoice;
}

/** The edit form's start: the session as the list shows it. `running` and `processed` read as played. */
export function editFormFor(session: SessionRow): EditSessionForm {
  return {
    title: session.title,
    number: session.number === null ? '' : String(session.number),
    realDate: session.realDate ?? '',
    status:
      session.status === 'planned' || session.status === 'prepped' ? session.status : 'played',
  };
}

export type EditSessionResult =
  | {
      readonly ok: true;
      /** The new title, when it changed (a metadata write: `sessions.update` has no title). */
      readonly title: string | null;
      /** The `sessions.update` body, when the number, date or status changed. */
      readonly update: SessionUpdateV1 | null;
    }
  | { readonly ok: false; readonly problem: SessionFormProblem };

/** What an edit changes, as the requests to send; only changed fields are sent. */
export function editSessionRequest(session: SessionRow, form: EditSessionForm): EditSessionResult {
  const checked = checkSessionForm(form);
  if (!checked.ok) return checked;
  const { title, number, realDate } = checked;
  const update: { -readonly [K in keyof SessionUpdateV1]: SessionUpdateV1[K] } = {};
  if (number !== session.number) update.number = number;
  if ((realDate === '' ? null : realDate) !== session.realDate) {
    update.realDate = realDate === '' ? null : realDate;
  }
  if (form.status !== editFormFor(session).status) update.status = form.status;
  return {
    ok: true,
    title: title === session.title ? null : title,
    update: Object.keys(update).length === 0 ? null : update,
  };
}

export const EDIT_REFUSED_TEXT = 'Check the title and number.';
export const EDIT_FAILED_TEXT = "The session couldn't be saved. Try again.";

export function editSessionError(error: unknown): string {
  return error instanceof ApiError && error.status === 422 ? EDIT_REFUSED_TEXT : EDIT_FAILED_TEXT;
}
